import { useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { IconSelect } from "@/components/admin/IconSelect";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { useCreateSocialLink, useDeleteSocialLink, useSocialLinks, useUpdateSocialLink } from "@/features/social-links/hooks";
import type { SocialLink, SocialLinkInput } from "@/features/social-links/types";

const emptyForm: SocialLinkInput = {
  platform: "",
  label: "",
  iconName: "",
  url: "",
  sortOrder: 0,
  isActive: true,
};

export function SocialLinksPage() {
  const currentUser = useCurrentUser();
  const links = useSocialLinks();
  const createLink = useCreateSocialLink();
  const updateLink = useUpdateSocialLink();
  const deleteLink = useDeleteSocialLink();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedLink, setSelectedLink] = useState<SocialLink | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SocialLink | null>(null);
  const [form, setForm] = useState<SocialLinkInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");

  const sortedLinks = useMemo(
    () => [...(links.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.platform.localeCompare(b.platform)),
    [links.data],
  );

  function openCreate() {
    setSelectedLink(null);
    setForm(emptyForm);
    setError(null);
    setDrawerMode("create");
  }

  function openEdit(link: SocialLink) {
    setSelectedLink(link);
    setForm({
      platform: link.platform,
      label: link.label ?? "",
      iconName: link.iconName ?? "",
      url: link.url,
      sortOrder: link.sortOrder,
      isActive: link.isActive,
    });
    setError(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedLink(null);
    setForm(emptyForm);
    setError(null);
  }

  function validateUrl(value: string) {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.platform.trim()) {
      setError("Platform is required.");
      return;
    }
    if (!validateUrl(form.url)) {
      setError("Enter a valid http or https URL.");
      return;
    }

    if (drawerMode === "create") {
      createLink.mutate(form, {
        onSuccess: closeDrawer,
        onError: (err) => setError(err instanceof Error ? err.message : "Unable to create social link."),
      });
      return;
    }

    if (selectedLink) {
      updateLink.mutate(
        { link: selectedLink, input: form },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to update social link."),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteLink.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Social Links"
        description="Manage public active social links. Public reads only return links marked active."
        actions={<Button onClick={openCreate}>New Social Link</Button>}
      />
      {links.isError && (
        <div className="rounded-md border border-red-300/30 bg-red-500/10 p-4 text-sm text-red-100">
          {links.error instanceof Error ? links.error.message : "Unable to load social links."}
        </div>
      )}
      <AdminDataTable
        rows={sortedLinks}
        getRowKey={(link) => link.id}
        emptyState={
          <EmptyState
            title={links.isLoading ? "Loading social links..." : "No social links yet"}
            description="Create the first link to start building the public socials list."
            action={!links.isLoading && <Button onClick={openCreate}>Create Social Link</Button>}
          />
        }
        columns={[
          {
            key: "platform",
            header: "Platform",
            render: (link) => (
              <div>
                <p className="font-medium text-white">{link.platform}</p>
                <p className="mt-1 text-xs text-content-muted">{link.iconName || "No icon"}</p>
              </div>
            ),
          },
          {
            key: "url",
            header: "URL",
            render: (link) => (
              <a href={link.url} target="_blank" rel="noreferrer" className="text-cyan-100 hover:text-cyan-300">
                {link.url}
              </a>
            ),
          },
          {
            key: "sortOrder",
            header: "Sort",
            render: (link) => link.sortOrder,
          },
          {
            key: "status",
            header: "Status",
            render: (link) => <StatusBadge tone={link.isActive ? "active" : "inactive"}>{link.isActive ? "Active" : "Inactive"}</StatusBadge>,
          },
          {
            key: "actions",
            header: "Actions",
            className: "px-4 py-4 align-middle text-right",
            render: (link) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(link)}>
                  Edit
                </Button>
                {canDelete && (
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(link)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <AdminFormDrawer
        isOpen={drawerMode !== null}
        title={drawerMode === "create" ? "New Social Link" : "Edit Social Link"}
        description="Inactive links remain in admin but are excluded from the public endpoint."
        onClose={closeDrawer}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Platform Name" value={form.platform} onChange={(value) => setForm((current) => ({ ...current, platform: value }))} required />
          <TextField label="URL" value={form.url} type="url" onChange={(value) => setForm((current) => ({ ...current, url: value }))} required />
          <IconSelect
            label="Icon"
            iconSet="social"
            value={form.iconName ?? ""}
            onChange={(value) => setForm((current) => ({ ...current, iconName: value }))}
          />
          <TextField label="Label" value={form.label ?? ""} onChange={(value) => setForm((current) => ({ ...current, label: value }))} />
          <TextField
            label="Sort Order"
            value={String(form.sortOrder ?? 0)}
            type="number"
            onChange={(value) => setForm((current) => ({ ...current, sortOrder: Number(value) }))}
          />
          <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
            <input
              className="size-4 accent-cyan-300"
              type="checkbox"
              checked={Boolean(form.isActive)}
              onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
            />
            <span className="text-sm text-content-secondary">Active on public site</span>
          </label>
          {!canDelete && <p className="text-xs text-content-muted">Only Admin, Owner, and Developer roles can delete social links.</p>}
          {error && <p className="rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" disabled={createLink.isPending || updateLink.isPending}>
              {createLink.isPending || updateLink.isPending ? "Saving..." : "Save Social Link"}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Social Link"
        description={`Delete "${deleteTarget?.platform ?? "this social link"}"? This is a hard delete in the current API.`}
        isPending={deleteLink.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url" | "number";
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
      />
    </label>
  );
}
